import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { OLPaper } from "OLPaper";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { useMessage } from "hooks/intl";
import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { TransactionBackendModel } from "types/transactions";
import { KeyValuePair } from "utilities/objects";
import { useStyles } from "./SingleTransaction.styles";

type ExtendedKeyValue = KeyValuePair & {
  "data-test-id": string;
};

interface Props {
  transactionData?: TransactionBackendModel;
}

export function CustomerDetailsSection({ transactionData }: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const isLoading = !transactionData;

  const fields: ExtendedKeyValue[] = useMemo(
    () => [
      {
        key: message({ id: "fullName" }),
        value: transactionData?.customerData?.name,
        "data-test-id": "transaction-customer-fullname",
      },
      {
        key: message({ id: "emailAddress" }),
        value: transactionData?.customerData?.email,
        "data-test-id": "transaction-customer-email",
      },
      {
        key: message({ id: "phoneNumber" }),
        value: transactionData?.customerData?.phone,
        "data-test-id": "transaction-customer-phone",
      },
      {
        key: message({ id: "loyaltyCardNumber" }),
        value: transactionData?.customerData?.loyaltyCardNumber,
        "data-test-id": "transaction-customer-loyalty-card-number",
      },
      {
        key: message({ id: "street" }),
        value: transactionData?.customerData?.address?.street,
        "data-test-id": "transaction-customer-street",
      },
      {
        key: message({ id: "postalCode" }),
        value: transactionData?.customerData?.address?.postal,
        "data-test-id": "transaction-customer-postal-code",
      },
      {
        key: message({ id: "city" }),
        value: transactionData?.customerData?.address?.city,
        "data-test-id": "transaction-customer-city",
      },
      {
        key: message({ id: "state" }),
        value: transactionData?.customerData?.address?.province,
        "data-test-id": "transaction-customer-state",
      },
      {
        key: message({ id: "country" }),
        value: transactionData?.customerData?.address?.country,
        "data-test-id": "transaction-customer-country",
      },
    ],
    [transactionData, message],
  );

  return (
    <Grid item xs={5} display="flex" flexDirection="column">
      <Box className={classes.textWithTooltipBox}>
        <Typography className={classes.sectionTitle}>
          {message({ id: "customerDetailsFromTheTransaction" })}
        </Typography>
        <TooltipWithIcon
          className={classes.tooltip}
          isWhite
          text={
            <>
              <Typography className={classes.tooltipTitle}>
                {message({ id: "customerDetailsFromTheTransaction" })}
              </Typography>
              <Typography className={classes.tooltipDescription}>
                {message({ id: "customerDetailsFromTheTransactionInfo" })}
              </Typography>
            </>
          }
        />
      </Box>
      <OLPaper className={`${classes.details} ${classes.detailsSection}`}>
        <Grid container className={classes.gridContainer} rowSpacing={8}>
          {fields.map(
            (
              { key, value, "data-test-id": dataTestId }: ExtendedKeyValue,
              index: number,
            ) => (
              <Grid item xs={12} key={index}>
                <Typography
                  data-test-id={`${dataTestId}-key`}
                  className={classes.smallText}
                >
                  {key}
                </Typography>
                <Typography
                  data-test-id={`${dataTestId}-value`}
                  className={classes.largeText}
                  data-hj-suppress
                >
                  {isLoading ? <Skeleton width={100} /> : value || "-"}
                </Typography>
              </Grid>
            ),
          )}
        </Grid>
      </OLPaper>
    </Grid>
  );
}
